<script
  generic="
    TValues extends DataTableProps['value'],
    TRow = TValues extends readonly (infer I)[] ? I : never
  "
  lang="ts"
  setup
>
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import VPopover from '@shared/component/VPopover/VPopover.vue';
import type { DataTableFilterMetaData, DataTableProps } from 'primevue/datatable';
import type { ComputedRef } from 'vue';
import { computed, ref } from 'vue';
import type { ActiveFilters, FilterVisualMeta, Property, ValueColors } from './types';
import VDataTableFiltersButton from './VDataTableFiltersButton.vue';

const props = defineProps<{
  properties: Array<Property<keyof TRow>>;
  values: TValues;
  valueColors?: ValueColors<TRow>;
  showCounts?: boolean;
}>();

const model = defineModel<Partial<Record<keyof TRow, DataTableFilterMetaData>>>({ default: {} });

const popover = ref<InstanceType<typeof VPopover>>();

const activeFilters: ComputedRef = computed(() =>
  Object.entries(model.value)
    // Toon alleen pills voor velden die deze popover zelf aanbiedt; filters die buiten de popover
    // worden beheerd (bijv. losse statusknoppen) krijgen geen verwijderbare pill.
    .filter(([field]) => field !== 'global' && props.properties.some((p) => p.field === field))
    .reduce(
      (previousValue, [field, filterValue]) => [
        ...previousValue,
        ...(filterValue as DataTableFilterMetaData).value.map((value: any) => ({
          field,
          value,
        })),
      ],
      [] as ActiveFilters<keyof TRow>[],
    ),
);

const filters = computed(() =>
  props.properties
    .map((property) => {
      // Velden met meerdere waarden (arrays) worden platgeslagen zodat elke
      // waarde een eigen filteroptie krijgt.
      const allValues =
        props.values?.flatMap((value) => {
          const fieldValue = value[property.field];
          return Array.isArray(fieldValue) ? fieldValue : [fieldValue];
        }) ?? [];
      const values = Array.from(new Set(allValues)).sort();

      return {
        property,
        values: values.map((v) => {
          const visual = props.valueColors?.[property.field]?.[v] as FilterVisualMeta | undefined;

          return {
            label: formatValue(v),
            value: v,
            count: allValues.filter((x) => x === v).length,
            prefixIcon: visual?.icon,
            iconClass: visual?.iconClass,
          };
        }),
      };
    })
    .filter(({ values }) => values.length > 1),
);

function addModelValue(field: keyof TRow, value: string) {
  const matchMode = props.properties.find((p) => p.field === field)?.matchMode ?? 'in';

  model.value = {
    ...model.value,
    [field]: { matchMode, value: [...(model.value[field]?.value || []), value] },
  };
}

function clearAllFilters() {
  model.value = {} as Record<keyof TRow, DataTableFilterMetaData>;
}

const getFilterActive = (field: keyof TRow, value: string) =>
  field in model.value && model.value[field]?.value.includes(value);

function removeModelValue(field: keyof TRow, value: string) {
  const newModel = { ...model.value };

  if (newModel[field]?.value.length <= 1) {
    delete newModel[field];
  } else {
    newModel[field]?.value.splice(newModel[field].value.indexOf(value), 1);
  }

  model.value = newModel;
}

function toggleModelValue(field: keyof TRow, value: string) {
  if (getFilterActive(field, value)) {
    removeModelValue(field, value);
  } else {
    addModelValue(field, value);
  }
}

function getVisualMeta(field: keyof TRow, value: string): FilterVisualMeta | undefined {
  const meta = props.valueColors?.[field]?.[value];
  return typeof meta === 'object' && meta !== null ? (meta as FilterVisualMeta) : undefined;
}

function toggleOverlay(event: Event) {
  popover.value?.toggle(event);
}

function formatValue(value: any): string {
  return value === true ? 'Ja' : value === false ? 'Nee' : value || '-';
}

function getBasicColorValue(field: keyof TRow, value: string): string | undefined {
  const raw = props.valueColors?.[field]?.[value] as unknown;

  if (typeof raw === 'string') {
    return raw;
  }

  return (raw as FilterVisualMeta)?.color;
}
</script>

<template>
  <div
    v-if="filters.length"
    class="flex flex-wrap items-center gap-1 md:min-h-12"
    role="group"
    aria-label="TableFilters"
  >
    <ExpandTransition direction="horizontal" group>
      <VDataTableFiltersButton
        v-for="{ field, value } in activeFilters"
        :key="`${field}-${value}`"
        shadow
        :color="getBasicColorValue(field, value)"
        :prefix-icon="getVisualMeta(field, value)?.icon"
        :icon-class="getVisualMeta(field, value)?.iconClass"
        :suffix-icon="['far', 'xmark']"
        @click="removeModelValue(field, value)"
      >
        <span class="max-w-[35ch] truncate">{{ formatValue(value) }}</span>
      </VDataTableFiltersButton>
    </ExpandTransition>

    <VDataTableFiltersButton
      class="font-bold"
      shadow
      :suffix-icon="['far', 'xmark']"
      v-if="activeFilters.length > 1"
      @click="clearAllFilters"
    >
      Filters wissen
    </VDataTableFiltersButton>

    <VDataTableFiltersButton
      class="font-bold"
      :inverted="!!activeFilters.length"
      :prefix-icon="['far', 'plus']"
      :shadow="!activeFilters.length"
      @click="toggleOverlay"
    >
      Filter
    </VDataTableFiltersButton>

    <VPopover ref="popover">
      <div class="flex flex-wrap gap-x-2 gap-y-4 p-3">
        <div v-for="{ property, values } of filters" class="grow" :key="property.field">
          <div class="mb-2 text-sm text-primary-900">
            {{ property.header }}
          </div>

          <div class="grid max-h-56 scrollbar-thin gap-2 overflow-auto pr-2">
            <VDataTableFiltersButton
              v-for="opt of values"
              :active="getFilterActive(property.field, opt.value)"
              class="font-bold"
              :color="getBasicColorValue(property.field, opt.value)"
              :key="opt.value"
              :prefix-icon="opt.prefixIcon"
              :icon-class="opt.iconClass"
              @click="toggleModelValue(property.field, opt.value)"
            >
              <span class="max-w-[35ch]">{{ opt.label }}</span>
              <span v-if="props.showCounts" class="text-grey-400"> ({{ opt.count }}) </span>
            </VDataTableFiltersButton>
          </div>
        </div>
      </div>
    </VPopover>
  </div>
</template>
